import { computed, DestroyRef, effect, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { distinctUntilChanged, filter, firstValueFrom, map, shareReplay, tap } from 'rxjs';

import type { Maybe } from '@evc/web-components';

import { PlatformConfigService } from '../config/config.service';
import type { I18nConfig } from '../config/config.type';
import { AvailableLanguages } from '../config/env.type';

/** storing locale may require consent
 * TODO : implement consent check - always true today
 */
const TODO_CONSENT_STORAGE_LOCALE = true;

type LanguageSources = {
  // if setted directly here (setLanguage)
  current: Maybe<string>;
  // sure source of thruth (?lang, config)
  sure: Maybe<string>;
  // user pref locale - if need auth may wait for it
  user: string | 'WAIT_API' | undefined;
  // potential source of thruth - but user pref would be better
  potential: Maybe<string>;
  // may fallback to unsafe sources - never if use auth (need wait user)
  fallback: Maybe<string>;
};

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  #i18nConfig: I18nConfig = inject(PlatformConfigService).get('i18n');
  #destroyRef = inject(DestroyRef);

  /** may guess from many different sources - ordered by importance */
  #langSources = signal<LanguageSources>({
    current: undefined,
    sure: undefined,
    user: undefined,
    potential: undefined,
    fallback: undefined,
  });

  /** computed lang from source - please do not touch */
  readonly lang = computed(() => {
    const { current, user, sure, potential, fallback } = this.#langSources();
    const mustWaitUser = user === 'WAIT_API';
    const maybeFallbacks = mustWaitUser ? undefined : fallback;
    const maybeUser = mustWaitUser ? undefined : user;

    const hasCandidate = !!(current || sure || maybeUser || potential || maybeFallbacks);
    const fallbackPlatformLangFallback = hasCandidate
      ? this.#i18nConfig.platformLanguages[0]
      : undefined;

    return this.#ensureLangIsValid([
      current,
      sure,
      maybeUser,
      potential,
      maybeFallbacks,
      fallbackPlatformLangFallback,
    ]);
  });

  /** ensure locale compatible with platform - some services are limited (login, api errors...) */
  platformSafeLang = computed<AvailableLanguages>(() => {
    const lang = this.lang()?.toLocaleLowerCase();

    const isAvaible = () => {
      if (!lang || lang === '') return false;

      return this.#i18nConfig.platformLanguages.includes(lang as AvailableLanguages);
    };

    return isAvaible() ? (lang as AvailableLanguages) : this.#i18nConfig.platformLanguages[0];
  });

  readonly locale = computed(() => (this.lang() === 'fr' ? 'fr-FR' : 'en-US'));

  get otherLang(): Maybe<string> {
    const ONLY_IF_2_LOCALES = this.#i18nConfig.languages.length === 2;

    return ONLY_IF_2_LOCALES
      ? this.#i18nConfig.languages.filter((lang: string) => lang !== this.lang())[0]
      : undefined;
  }

  readonly lang$ = toObservable(this.lang).pipe(
    takeUntilDestroyed(this.#destroyRef),
    filter((lang) => !!lang),
    map((lang) => lang!),
    distinctUntilChanged(),
    shareReplay({
      bufferSize: 1,
      refCount: true,
    }),
    tap((lang) => this.#updateHTMLLangAttribute(lang)),
  );

  #skipFirstChangeLang = true;
  #storeLangOnChange = effect(() => {
    const lang = this.lang();
    if (!lang && this.#skipFirstChangeLang) {
      this.#skipFirstChangeLang = false;

      return;
    }
    this.#setLangInStorage(lang);
  });

  constructor() {
    this.#destroyRef.onDestroy(() => {
      this.#storeLangOnChange.destroy();
    });
  }

  /**
   * @param withAuth - if true, may wait for user profile to be ready to use preferedLanguage
   */
  async init(withAuth = false): Promise<string> {
    this.#guessLang(withAuth);

    return firstValueFrom(this.lang$, { defaultValue: this.platformSafeLang() });
  }

  /** once defined - will override any other sources */
  setLanguage(lang?: string): void {
    this.#safelySetLanguage(lang!, 'current');
  }

  /** specific for user pref - may be overrided by better sources */
  setUserLanguage(lang?: string): void {
    const oldValue = (({ user }) => (user === 'WAIT_API' ? undefined : user))(this.#langSources());
    const updated = this.#safelySetLanguage(lang!, 'user');

    if (!updated) {
      // stop waiting for user prefLang
      this.#langSources.update((guesses) => ({
        ...guesses,
        user: oldValue,
      }));
    }
  }

  /** may switch lang - only if 2 locales - of please specify witch lang */
  changeLanguage(lang: Maybe<string> = this.otherLang): Maybe<string> {
    const ensuredLang = lang || this.otherLang;
    if (!ensuredLang) return undefined;
    this.setLanguage(ensuredLang);

    return ensuredLang;
  }

  /** Returns first valid lang from array of candidates */
  #ensureLangIsValid(lang?: Maybe<string> | Maybe<string>[]): Maybe<string> {
    const candidates = Array.isArray(lang) ? lang : [lang];

    return candidates
      .map((candidate) =>
        candidate && candidate !== '' ? candidate.toLocaleLowerCase() : undefined,
      )
      .find((ensuredLang) => ensuredLang && this.#i18nConfig.languages.includes(ensuredLang));
  }

  /** may update (async) our language
   * @returns change-succeed : boolean
   */
  #safelySetLanguage(lang: string, type: keyof LanguageSources): boolean {
    const current = this.#langSources()[type];
    const ensuredLang = this.#ensureLangIsValid(lang);
    if (!ensuredLang || ensuredLang === current) return false;

    this.#langSources.update((guesses) => ({
      ...guesses,
      [type]: ensuredLang,
    }));

    return true;
  }

  #guessLang(withAuth: boolean): Maybe<string> {
    const sources = {
      config: () => this.#i18nConfig.lang,
      url: () => new URL(window.location.href).searchParams.get('lang') ?? undefined,
      storage: () => this.#getLangFromStorage() ?? undefined,
      browser: () => this.#getLangFromBrowser() ?? undefined,
      defaut: () => this.#i18nConfig.defaultLang,
    };
    const sure = sources.config() || sources.url();
    const potential = sources.storage();
    const fallback = sources.browser() || sources.defaut();
    const user = withAuth ? 'WAIT_API' : undefined;

    this.#langSources.update((guesses) => ({
      ...guesses,
      user,
      sure,
      potential,
      fallback,
    }));

    return withAuth ? sure || potential : sure || potential || fallback;
  }

  #getLangFromBrowser(): string | null {
    const locale = navigator.languages
      ? navigator.languages[0]
      : // eslint-disable-next-line @typescript-eslint/no-explicit-any
        navigator.language || ((navigator as any).userLanguage as string);

    return locale?.split('-')[0];
  }

  #getLangFromStorage(): string | null {
    const { storageKey } = this.#i18nConfig;

    return localStorage.getItem(storageKey);
  }

  #setLangInStorage(lang: Maybe<string>): void {
    if (!TODO_CONSENT_STORAGE_LOCALE) return;

    const { storageKey } = this.#i18nConfig;
    if (!lang) return localStorage.removeItem(storageKey);

    localStorage.setItem(storageKey, lang);
  }

  #updateHTMLLangAttribute(lang: Maybe<string>): void {
    if (!lang) return;

    document.querySelector('html')!.setAttribute('lang', lang);
  }
}
